import React, { useEffect, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  CircularProgress,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import { useTranslation } from 'react-i18next';
import api from '../../../api/client';
import {
  bankItemToJdQuestion,
  getQuestionTypeColor,
  getQuestionTypeOptions,
} from '../pages/questionBank/questionBankUtils';

export default function QuestionBankPickerModal({
  open,
  onClose,
  onAddQuestions,
  existingQuestions,
  companyId,
}) {
  const { t } = useTranslation();
  const questionTypeOptions = getQuestionTypeOptions(t);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [questionType, setQuestionType] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  const existingBankIds = new Set(
    (existingQuestions || [])
      .map((question) => (typeof question === 'object' ? question.questionBankId : null))
      .filter(Boolean)
  );

  useEffect(() => {
    if (!open) {
      setQuestions([]);
      setSelectedIds([]);
      setSearch('');
      setQuestionType('');
      return;
    }

    const fetchQuestions = async () => {
      setLoading(true);
      try {
        const params = { page_size: 100 };
        if (search.trim()) params.search = search.trim();
        if (questionType) params.question_type = questionType;
        if (companyId) params.company = companyId;
        const response = await api.get('question-bank/', { params });
        const results = Array.isArray(response.data) ? response.data : (response.data.results || []);
        setQuestions(results);
      } catch (error) {
        setQuestions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [open, search, questionType, companyId]);

  const toggleSelected = (id) => {
    if (existingBankIds.has(id)) return;
    setSelectedIds((prev) => (
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]
    ));
  };

  const handleAdd = () => {
    const selected = questions
      .filter((question) => selectedIds.includes(question.id))
      .map(bankItemToJdQuestion);
    if (selected.length === 0) return;
    onAddQuestions(selected);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} sx={{ zIndex: 13000 }}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', md: 720 },
          maxHeight: '85vh',
          bgcolor: 'background.paper',
          borderRadius: '16px',
          boxShadow: 24,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)',
            color: 'white',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <LibraryBooksOutlinedIcon />
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
              {t('questionBank.addFromBank', 'Add from Question Bank')}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, overflow: 'hidden' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 180px' }, gap: 2 }}>
            <TextField
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('questionBank.searchPlaceholder', 'Search questions...')}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            />
            <FormControl size="small" fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}>
              <InputLabel id="qb-picker-question-type-label">{t('jobDescription.questionType', 'Question Type')}</InputLabel>
              <Select
                labelId="qb-picker-question-type-label"
                value={questionType}
                label={t('jobDescription.questionType', 'Question Type')}
                onChange={(e) => setQuestionType(e.target.value)}
                sx={{ borderRadius: '12px' }}
                MenuProps={{
                  sx: { zIndex: 14000 },
                  PaperProps: {
                    sx: {
                      zIndex: 14000,
                      maxHeight: 280,
                      borderRadius: '12px',
                      mt: 0.5,
                      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                    },
                  },
                }}
              >
                <MenuItem value="">{t('questionBank.allTypes', 'All types')}</MenuItem>
                {questionTypeOptions.map((type) => (
                  <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ overflowY: 'auto', maxHeight: '46vh', pr: 1 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={32} />
              </Box>
            ) : questions.length === 0 ? (
              <Typography sx={{ color: 'text.secondary', textAlign: 'center', py: 6 }}>
                {t('questionBank.empty', 'No questions in the Question Bank yet.')}
              </Typography>
            ) : (
              <div className="space-y-2">
                {questions.map((question) => {
                  const alreadyAdded = existingBankIds.has(question.id);
                  const checked = alreadyAdded || selectedIds.includes(question.id);
                  return (
                    <button
                      type="button"
                      key={question.id}
                      onClick={() => toggleSelected(question.id)}
                      disabled={alreadyAdded}
                      className={`w-full text-left p-3 rounded-xl border-2 flex items-start gap-2 transition-colors ${
                        alreadyAdded
                          ? 'bg-gray-50 border-gray-200 opacity-60 cursor-not-allowed'
                          : checked
                            ? 'bg-blue-50 border-blue-300'
                            : 'bg-white border-gray-200 hover:border-blue-200'
                      }`}
                    >
                      <Checkbox checked={checked} disabled={alreadyAdded} sx={{ p: 0.5, mt: 0.25 }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getQuestionTypeColor(question.question_type)}`}>
                            {questionTypeOptions.find((opt) => opt.value === question.question_type)?.label || question.question_type}
                          </span>
                          {alreadyAdded && (
                            <span className="text-xs text-gray-500">
                              {t('questionBank.alreadyAdded', 'Already added')}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{question.text}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
            <Button onClick={onClose} sx={{ textTransform: 'none' }}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              variant="contained"
              disabled={selectedIds.length === 0}
              onClick={handleAdd}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px' }}
            >
              {t('questionBank.addSelectedCount', 'Add selected ({{count}})', { count: selectedIds.length })}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}
